import { IconsEnum } from '../../../../assets/icons/types';
import { Icon } from '../../../../helpers/Icon';
import { InputField } from '../../../../helpers/FormFields/InputField';
import { Popover } from '../../../../helpers/Popover';
import { PureTooltipIconRefButtonHelper } from '../../../../helpers/buttons/PureTooltipIconRefButtonHelper';
import { RefButtonHelper } from '../../../../helpers/buttons/RefButtonHelper';
import { useDropdownState } from '../../../../helpers/dropdowns/hooks/useDropdownState';
import { PopoverPlacement } from '../../../../helpers/Popover/popoverConstants';
import { TooltipPlacement } from '../../../../helpers/tooltips/tooltipsConstants';
import { Translate } from '../../../../helpers/Translate';
import { modelViewersKeys } from '../../../../locales/keys';

import { composerAspectRatios } from '../../composerConstants';
import { ComposerAspectRatioValue } from '../../composerTypes';

interface ComposerAspectRatioButtonProps {
  buttonClassName?: string;
  aspectRatio: ComposerAspectRatioValue;
  onSelectAspectRatio: (value: ComposerAspectRatioValue) => void;
  customWidth: number;
  customHeight: number;
  onCustomWidthChange: (value: number) => void;
  onCustomHeightChange: (value: number) => void;
  requestedWidth: number;
  requestedHeight: number;
  onRequestedWidthChange: (value: number) => void;
  onRequestedHeightChange: (value: number) => void;
}

const itemClass =
  'flex w-full items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700';

function ComposerAspectRatioButton({
  buttonClassName,
  aspectRatio,
  onSelectAspectRatio,
  customWidth,
  customHeight,
  onCustomWidthChange,
  onCustomHeightChange,
  requestedWidth,
  requestedHeight,
  onRequestedWidthChange,
  onRequestedHeightChange
}: ComposerAspectRatioButtonProps) {
  const {
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover
  } = useDropdownState();

  return (
    <div className="relative inline-flex">
      <PureTooltipIconRefButtonHelper
        ref={referencePopoverCallbackRef}
        icon={IconsEnum.CROP_SOLID}
        iconClassName="h-5 w-5"
        className={
          buttonClassName ||
          'py-2 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
        }
        tooltipI18nText={modelViewersKeys.aspectRatio}
        tooltipPlacement={TooltipPlacement.TOP}
        onClick={togglePopover}
      />
      {isPopoverOpen && (
        <Popover
          referenceElement={referencePopoverElement}
          placement={PopoverPlacement.TOP_START}
          distanceOffset={6}
          className="min-w-48 z-50 overflow-y-auto bg-white dark:bg-gray-800 border border-transparent focus:outline-none py-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700"
          closeOnOuterClick
          closePopover={closePopover}
        >
          {composerAspectRatios.map(({ value, label, i18nLabel }) => (
            <RefButtonHelper
              key={value}
              className={itemClass}
              onClick={() => onSelectAspectRatio(value)}
            >
              {i18nLabel ? <Translate id={i18nLabel} /> : label}
              {aspectRatio === value && (
                <Icon
                  className="h-4 w-4 text-blue-600"
                  icon={IconsEnum.CHECK_OUTLINE}
                />
              )}
            </RefButtonHelper>
          ))}

          {aspectRatio === ComposerAspectRatioValue.CUSTOM && (
            <div className="flex items-center px-4 pb-2 pt-1">
              <div className="w-16 flex">
                <InputField
                  name="composerAspectRatioCustomWidth"
                  value={customWidth.toString()}
                  onChange={(e) => onCustomWidthChange(+e.target.value)}
                  type="number"
                  min={1}
                  max={9999}
                  step={1}
                />
              </div>
              <span className="text-xs text-gray-500 w-3 text-center">:</span>
              <div className="w-16 flex">
                <InputField
                  name="composerAspectRatioCustomHeight"
                  value={customHeight.toString()}
                  onChange={(e) => onCustomHeightChange(+e.target.value)}
                  type="number"
                  min={1}
                  max={9999}
                  step={1}
                />
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

          <div className="px-4 pt-1 pb-2">
            <div className="text-2xs text-gray-500 mb-1">Render size</div>
            <div className="flex items-center gap-2">
              <div className="w-20 flex">
                <InputField
                  name="composerAspectRatioRequestedWidth"
                  value={requestedWidth.toString()}
                  onChange={(e) => onRequestedWidthChange(+e.target.value)}
                  type="number"
                  min={1}
                  max={8192}
                  step={1}
                />
              </div>
              <span className="text-xs text-gray-400 shrink-0">×</span>
              <div className="w-20 flex">
                <InputField
                  name="composerAspectRatioRequestedHeight"
                  value={requestedHeight.toString()}
                  onChange={(e) => onRequestedHeightChange(+e.target.value)}
                  type="number"
                  min={1}
                  max={8192}
                  step={1}
                />
              </div>
            </div>
          </div>
        </Popover>
      )}
    </div>
  );
}

export default ComposerAspectRatioButton;
